import * as React from 'react';

import IInputCallbackData from '@moosend/mooskin/lib/_utils';

import Filter from '../filter/Filter';

import { IConditionFilter, IFilterSelectOptions, IWebsites } from '../../model';

import { IAccountPreferences } from '../../../../../accounts/src/model';
import { Button, Box, Loader, Text, IconButton, variables } from '@moosend/mooskin';
import { QueryClient, QueryClientProvider } from 'react-query';
export interface IFilterCollection {
	criteriaOptions: IFilterSelectOptions[];
	filters: IConditionFilter[];
	isLoading?: boolean;
	style?: React.CSSProperties;
	className?: string;
	dataLabel: string;
	operator: 'all' | 'any';
	onAddCondition?: (dataLabel: string) => void;
	onConditionChange: (data: IInputCallbackData, dataLabel: string, id: string) => void;
	handleMissingData: (dataLabel: string) => void;
	forSegments?: boolean;
	segmentType?: string;
	websites?: IWebsites[];
	addBtnText?: string;
	dateFormat?: IAccountPreferences;
	separatorWithLine?: boolean;
	getSegmentSuggestions?: (field: string, params?: Object) => Promise<any>;
}

const operators = {
	all: 'And',
	any: 'Or',
};

const qc = new QueryClient({
	defaultOptions: {
		queries: { refetchOnWindowFocus: false },
	},
});

export const FilterCollection: React.FC<IFilterCollection> = (props) => {
	const onAddCondition = () => {
		props.onAddCondition && props.onAddCondition(props.dataLabel);
	};

	const onConditionChange = (data: IInputCallbackData, id: string) => {
		props.onConditionChange && props.onConditionChange(data, props.dataLabel, id);
	};

	const renderSeparator = () => {
		return props.separatorWithLine ? (
			<FilterSeparatorLine operator={props.operator} />
		) : (
			<FilterSeparator operator={operators[props.operator]} />
		);
	};

	const renderCollectionContent = () => {
		if (props.filters && props.filters.length) {
			const components = props.filters.map((filter: IConditionFilter, i) => {
				return (
					<Box key={i} position="relative" pb={props.forSegments ? 0 : 40}>
						<Filter
							criteriaOptions={props.criteriaOptions}
							onConditionChange={onConditionChange}
							forSegments={props.forSegments}
							websites={props.websites}
							dateFormat={props.dateFormat}
							segmentType={props.segmentType}
							getSegmentSuggestions={props.getSegmentSuggestions}
							{...filter}
						/>
						{i < props.filters.length - 1 && props.filters.length > 1 && renderSeparator()}
					</Box>
				);
			});

			return <FilterList handleMissingData={() => props.handleMissingData(props.dataLabel)}>{components}</FilterList>;
		}
	};

	const renderCollection = () => {
		return (
			<Box d="flex" direction="column">
				{renderCollectionContent()}
				{props.onAddCondition && (
					<Button
						onClick={onAddCondition}
						mt={props.filters && props.filters.length ? 30 : 0}
						disabled={props.criteriaOptions.length === 0 || props.segmentType?.includes('SystemGenerated')}
					>
						{props.addBtnText ? props.addBtnText : 'Add rule'}
					</Button>
				)}
			</Box>
		);
	};

	const renderLoader = () => {
		return (
			<Box d="flex" align="center" justify="center" minH={120}>
				<Loader />
			</Box>
		);
	};

	return <QueryClientProvider client={qc}>{props.isLoading ? renderLoader() : renderCollection()}</QueryClientProvider>;
};

export interface IFilterList {
	handleMissingData: () => void;
}

export const FilterList: React.FC<IFilterList> = (props) => {
	React.useEffect(() => {
		props.handleMissingData && props.handleMissingData();
	}, []);

	return <>{props.children}</>;
};

export const FilterSeparator: React.FC<{ operator?: string }> = (props) => {
	return (
		<Box
			borderTop="1px solid"
			borderLeft="1px solid"
			borderBottom="1px solid"
			borderTopColor="fontColors.text"
			borderLeftColor="fontColors.text"
			borderBottomColor="fontColors.text"
			position="absolute"
			left={-35}
			top={17}
			bottom={-18}
			d="flex"
			align="center"
			w={30}
		>
			<Text
				fontSize={12}
				fontWeight={500}
				fontColor="fontColors.white"
				w={30}
				h={30}
				bgColor="backgroundColors.primary2"
				d="flex"
				align="center"
				justify="center"
				borderRadius="50%"
				ml={-15}
			>
				{props.operator || 'AND'}
			</Text>
		</Box>
	);
};

export const FilterSeparatorLine: React.FC<{ operator: 'all' | 'any' }> = (props) => {
	return (
		<Box d="flex" align="center" justify="center" m="15px 0" w="97%">
			<Box flex={1} h={1} bgColor="backgroundColors.gray1" w="100%" />
			<Box d="flex" align="center" justify="center" w={36} textTransform="uppercase" fontSize={12} fontColor="fontColors.medgray1">
				{operators[props.operator].toUpperCase() || '&'}
			</Box>
			<Box flex={1} h={1} bgColor="backgroundColors.gray1" w="100%" />
		</Box>
	);
};

export default FilterCollection;
